import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { AlertTriangle, TrendingDown, Droplets, Activity, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const allAlerts = [
  {
    id: 1,
    title: 'High NRW in DMA-04',
    description: 'Non-revenue water exceeds 15% threshold',
    location: 'Panchayat C - DMA-04',
    type: 'nrw',
    severity: 'critical',
    status: 'active',
    timestamp: '2 hours ago',
    assignedTo: 'Field Team B',
    estimatedLoss: '₹1,200/day'
  },
  {
    id: 2,
    title: 'Nitrate Level Exceeded',
    description: 'Water quality parameter out of acceptable range',
    location: 'WTP-1 Output',
    type: 'quality',
    severity: 'critical',
    status: 'active',
    timestamp: '2 hours ago',
    assignedTo: 'Quality Team A',
    action: 'Investigation ongoing'
  },
  {
    id: 3,
    title: 'OHT-2 Storage Low',
    description: 'Storage level dropped below 50%',
    location: 'Panchayat B - OHT-2',
    type: 'supply',
    severity: 'warning',
    status: 'active',
    timestamp: '3 hours ago',
    assignedTo: 'Operations Team',
    currentLevel: '45%'
  },
  {
    id: 4,
    title: 'Flow Meter Malfunction',
    description: 'Inconsistent readings detected',
    location: 'DMA-07 Entry Point',
    type: 'technical',
    severity: 'warning',
    status: 'in-progress',
    timestamp: '5 hours ago',
    assignedTo: 'Maintenance Team',
    action: 'Technician dispatched'
  },
  {
    id: 5,
    title: 'Pressure Drop in Zone C',
    description: 'Distribution pressure below minimum threshold',
    location: 'Panchayat D - Zone C',
    type: 'supply',
    severity: 'warning',
    status: 'active',
    timestamp: '6 hours ago',
    assignedTo: 'Field Team A',
    pressure: '18 PSI (Min: 20 PSI)'
  },
  {
    id: 6,
    title: 'Leak Detection Alert',
    description: 'Potential pipeline leak identified',
    location: 'Between OHT-3 and DMA-08',
    type: 'leakage',
    severity: 'critical',
    status: 'in-progress',
    timestamp: '8 hours ago',
    assignedTo: 'Field Team C',
    estimatedLoss: '₹800/day'
  },
  {
    id: 7,
    title: 'Low Chlorine Residual',
    description: 'Chlorine level below minimum requirement',
    location: 'DMA-03 Entry',
    type: 'quality',
    severity: 'warning',
    status: 'in-progress',
    timestamp: '10 hours ago',
    assignedTo: 'Quality Team B',
    action: 'Dosing adjusted'
  },
  {
    id: 8,
    title: 'Storage Overflow at OHT-5',
    description: 'Storage overflow detected, check inlet valve',
    location: 'Panchayat E - OHT-5',
    type: 'overflow',
    severity: 'warning',
    status: 'resolved',
    timestamp: '1 day ago',
    assignedTo: 'Field Team B',
    resolvedBy: 'Field Team B',
    resolution: 'Inlet valve adjusted'
  },
  {
    id: 9,
    title: 'Turbidity Spike',
    description: 'Sudden increase in turbidity levels',
    location: 'WTP-2 Raw Water',
    type: 'quality',
    severity: 'normal',
    status: 'resolved',
    timestamp: '2 days ago',
    assignedTo: 'Quality Team A',
    resolvedBy: 'Quality Team A',
    resolution: 'Source water issue, resolved naturally'
  },
  {
    id: 10,
    title: 'Power Outage - Pump Station',
    description: 'Temporary supply disruption due to power failure',
    location: 'Panchayat A - Pump Station 2',
    type: 'technical',
    severity: 'critical',
    status: 'resolved',
    timestamp: '3 days ago',
    assignedTo: 'Operations Team',
    resolvedBy: 'Operations Team',
    resolution: 'Backup generator activated, power restored'
  },
];

const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'bg-destructive text-destructive-foreground';
    case 'warning': return 'bg-warning text-warning-foreground';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getSeverityBorderColor = (severity) => {
  switch (severity) {
    case 'critical': return 'border-destructive';
    case 'warning': return 'border-warning';
    default: return 'border-border';
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'nrw': return <TrendingDown className="w-5 h-5" />;
    case 'quality': return <Droplets className="w-5 h-5" />;
    case 'supply': return <Activity className="w-5 h-5" />;
    case 'leakage': return <AlertTriangle className="w-5 h-5" />;
    default: return <AlertTriangle className="w-5 h-5" />;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'active': return <AlertTriangle className="w-4 h-4" />;
    case 'in-progress': return <Clock className="w-4 h-4" />;
    case 'resolved': return <CheckCircle2 className="w-4 h-4" />;
    default: return <XCircle className="w-4 h-4" />;
  }
};

const AlertCard = ({ alert, onResolve, onAssign }) => {
  return (
    <div className={`p-4 rounded-lg border-l-4 ${getSeverityBorderColor(alert.severity)} bg-card border border-border hover:shadow-md transition-shadow`}>
      <div className="flex gap-4">
        {/* Icon */}
        <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${getSeverityColor(alert.severity)} bg-opacity-10 flex items-center justify-center`}>
          {getTypeIcon(alert.type)}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1">
              <h4 className="font-semibold text-foreground mb-1">{alert.title}</h4>
              <p className="text-sm text-muted-foreground">{alert.description}</p>
            </div>
            <Badge className={getSeverityColor(alert.severity)}>
              {alert.severity}
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground mb-3">
            <div className="flex items-center gap-1">
              <span className="font-medium">Location:</span>
              <span>{alert.location}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Time:</span>
              <span>{alert.timestamp}</span>
            </div>
            {alert.assignedTo && (
              <div className="flex items-center gap-1">
                <span className="font-medium">Assigned:</span>
                <span>{alert.assignedTo}</span>
              </div>
            )}
            {alert.estimatedLoss && (
              <div className="flex items-center gap-1">
                <span className="font-medium text-destructive">Est. Loss:</span>
                <span className="text-destructive font-semibold">{alert.estimatedLoss}</span>
              </div>
            )}
          </div>
          
          {alert.status === 'resolved' ? (
            <div className="p-3 bg-success/5 rounded border border-success/20">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <span className="text-sm font-medium text-success">Resolved by {alert.resolvedBy}</span>
              </div>
              <p className="text-xs text-muted-foreground pl-6">{alert.resolution}</p>
            </div>
          ) : (
            <div className="flex gap-2">
              {alert.status === 'active' && (
                <Button size="sm" variant="outline" onClick={() => onAssign(alert.id)}>
                  Assign Team
                </Button>
              )}
              <Button 
                size="sm" 
                variant={alert.status === 'in-progress' ? 'default' : 'outline'}
                onClick={() => onResolve(alert.id)}
              >
                {alert.status === 'in-progress' ? 'Mark Resolved' : 'Take Action'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const AlertsPanel = ({ selectedDistrict, selectedPanchayat }) => {
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  const activeAlerts = allAlerts.filter(a => a.status === 'active');
  const inProgressAlerts = allAlerts.filter(a => a.status === 'in-progress');
  const resolvedAlerts = allAlerts.filter(a => a.status === 'resolved');
  const criticalAlerts = allAlerts.filter(a => a.severity === 'critical' && a.status !== 'resolved');
  
  const handleResolve = (id) => {
    console.log('Resolving alert:', id);
    // In real app, update alert status
  };
  
  const handleAssign = (id) => {
    console.log('Assigning alert:', id);
    // In real app, show assignment dialog
  };
  
  const filterAlerts = (alerts) => {
    return alerts.filter(alert => {
      if (filterSeverity !== 'all' && alert.severity !== filterSeverity) return false;
      if (filterType !== 'all' && alert.type !== filterType) return false;
      return true;
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Alerts & Notifications</h1>
        <p className="text-muted-foreground">Monitor and manage system alerts and exceptions</p>
      </div>
      
      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Critical Alerts</p>
                <p className="text-3xl font-bold text-destructive">{criticalAlerts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Active Alerts</p>
                <p className="text-3xl font-bold text-warning">{activeAlerts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <Activity className="w-6 h-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">In Progress</p>
                <p className="text-3xl font-bold text-primary">{inProgressAlerts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Resolved (7d)</p>
                <p className="text-3xl font-bold text-success">{resolvedAlerts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Severity:</label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Type:</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="nrw">NRW</SelectItem>
                  <SelectItem value="quality">Quality</SelectItem>
                  <SelectItem value="supply">Supply</SelectItem>
                  <SelectItem value="leakage">Leakage</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Alerts Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Active ({activeAlerts.length})
          </TabsTrigger>
          <TabsTrigger value="in-progress" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            In Progress ({inProgressAlerts.length})
          </TabsTrigger>
          <TabsTrigger value="resolved" className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Resolved ({resolvedAlerts.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            All ({allAlerts.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="active" className="space-y-4">
          {filterAlerts(activeAlerts).map(alert => (
            <AlertCard 
              key={alert.id} 
              alert={alert} 
              onResolve={handleResolve}
              onAssign={handleAssign}
            />
          ))}
          {filterAlerts(activeAlerts).length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
                <p className="text-muted-foreground">No active alerts matching the filters</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="in-progress" className="space-y-4">
          {filterAlerts(inProgressAlerts).map(alert => (
            <AlertCard 
              key={alert.id} 
              alert={alert} 
              onResolve={handleResolve}
              onAssign={handleAssign}
            />
          ))}
          {filterAlerts(inProgressAlerts).length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No alerts in progress matching the filters</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="resolved" className="space-y-4">
          {filterAlerts(resolvedAlerts).map(alert => (
            <AlertCard 
              key={alert.id} 
              alert={alert} 
              onResolve={handleResolve}
              onAssign={handleAssign}
            />
          ))}
          {filterAlerts(resolvedAlerts).length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No resolved alerts matching the filters</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="all" className="space-y-4">
          {filterAlerts(allAlerts).map(alert => (
            <AlertCard 
              key={alert.id} 
              alert={alert} 
              onResolve={handleResolve}
              onAssign={handleAssign}
            />
          ))}
          {filterAlerts(allAlerts).length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No alerts matching the filters</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlertsPanel;